import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTwitchUserAndChannel } from "store/platforms/thunks";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { RootState } from "store/reducers";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { usePlatformActions } from "hooks/usePlatformActions";
import { useTranslation } from "react-i18next";
import { PlatformId } from "views/page-content/platforms/types";
import { updateGeneralForm } from "store/platforms/slice";
import { PlatformSubsettingsBase } from "../PlatformSubsettingsBase";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { TextInput } from "components/inputs/text-input/TextInput";
import { Spinner } from "components/spinners/Spinner";
import { BaseAutocomplete } from "components/inputs/autocomplete/base-autocomplete";
import { TwitchCategory } from "store/platforms/types";
import { twitchClient } from "api/twitch/twitch-client";
import { TwitchIngestServer } from "@switcherstudio/switcher-api-client";
import { useSettableMemo } from "hooks/useSettableMemo";
import { DestinationHeader } from "../../DestinationHeader";

interface TwitchContainerProps {
    setHasAddedPlatformSpecificSettings: (value: boolean) => void;
    setOnAdvanceStep: (callback: () => void) => void;
    setOnDismiss: (callback: () => void) => void;
    selectedDestinationId?: string | null;
}

export const TwitchPlatformSettings = ({
    setHasAddedPlatformSpecificSettings,
    setOnAdvanceStep,
    setOnDismiss,
    selectedDestinationId
}: TwitchContainerProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { user } = useSelector((s: RootState) => s.platforms.twitch);
    const { unlink, init } = usePlatformActions(PlatformId.Twitch);
    const { t } = useTranslation("platforms");
    const { generalForm } = useSelector(
        (state: RootState) => state.platforms.general
    );
    const { twitch } = useSelector((state: RootState) => state.platforms);

    // Selected destination state
    const [specificDestination, setSpecificDestination] =
        useState<DestinationDescriptor | null>(
            generalForm?.selectedSimulcastDestinations?.find(
                (dest) => dest.id === selectedDestinationId
            ) || null
        );

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function getChannelInfo() {
            setLoading(true);
            try {
                await init();
                await dispatch(getTwitchUserAndChannel());
            } catch (e) {
                await unlink();
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("platforms:token-expired", {
                            platformName: "Twitch"
                        })
                    })
                );
            } finally {
                setLoading(false);
            }
        }

        getChannelInfo();
    }, [dispatch, navigate, unlink, t, init]);

    // Category selection state
    const [selectedCategory, setSelectedCategory] =
        useState<TwitchCategory | null>(
            specificDestination?.platformSpecificSettings?.category || null
        );
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
    const [twitchCategories, setTwitchCategories] = useState<TwitchCategory[]>(
        []
    );
    const searchCategories = useCallback(async (query: string) => {
        let categories = [] as Array<TwitchCategory>;
        setCategoriesLoading(true);

        if (query.length >= 2) {
            try {
                const data = await twitchClient.searchCategories(query);
                categories = data || [];
            } catch (e) {
                // error notification about failing to search
            }
        }

        setTwitchCategories(categories);
        setCategoriesLoading(false);
    }, []);

    // Delay state
    const [delay, setDelay] = useState<number | null>(
        specificDestination?.platformSpecificSettings?.delay
    );
    const handleDelayChange = useCallback((value: string) => {
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
            setDelay(numValue);
        }
    }, []);

    // Ingest state
    const [ingestServer, setIngestServer] = useSettableMemo<TwitchIngestServer>(
        () =>
            specificDestination?.platformSpecificSettings?.ingestServer ??
            twitch.ingestServers[0],
        [twitch.ingestServers]
    );
    const onIngestChange = useCallback(
        (serverName: string) => {
            const selectedServer = twitch.ingestServers.find(
                (s) => s.name === serverName
            );
            setIngestServer(selectedServer);
        },
        [twitch.ingestServers, setIngestServer]
    );

    const saveSettings = useCallback(
        async (destination: DestinationDescriptor) => {
            const fullDestination: DestinationDescriptor = {
                name: destination.name,
                id: destination.id,
                platform: PlatformId.Twitch,
                platformSpecificSettings: {
                    category: selectedCategory,
                    ingestServer: ingestServer,
                    delay: delay
                }
            };

            // Preserve original order by mapping through the array and replacing the matching item
            const newSelectedDests =
                generalForm.selectedSimulcastDestinations.map((dest) =>
                    dest.id === fullDestination.id ? fullDestination : dest
                );

            // If the destination wasn't in the array, add it to the end
            if (
                !generalForm.selectedSimulcastDestinations.some(
                    (dest) => dest.id === fullDestination.id
                )
            ) {
                newSelectedDests.push(fullDestination);
            }

            dispatch(
                updateGeneralForm({
                    selectedSimulcastDestinations: newSelectedDests
                })
            );
        },
        [
            dispatch,
            generalForm.selectedSimulcastDestinations,
            selectedCategory,
            ingestServer,
            delay
        ]
    );

    useEffect(() => {
        const dismissCallback = () => setSpecificDestination(null);
        setOnDismiss(() => dismissCallback);

        if (!specificDestination) return;
        const callback = () => saveSettings(specificDestination);
        setOnAdvanceStep(() => callback);
    }, [specificDestination, setOnAdvanceStep, setOnDismiss, saveSettings]);

    if (loading) {
        return <Spinner />;
    }

    return (
        <>
            {!!specificDestination ? (
                <>
                    <DestinationHeader
                        specificDestination={specificDestination}
                    />
                    <BaseAutocomplete<TwitchCategory>
                        items={twitchCategories}
                        itemIdKey={"name"}
                        itemLabelKey={"name"}
                        itemImageKey={"box_art_url"}
                        labels={{
                            appliedItemsLabel: t("platforms:search-label"),
                            noItemsAppliedLabel: t(
                                "platforms:search-none-applied"
                            ),
                            inputLabel: t("platforms:search-placeholder")
                        }}
                        assignedItems={
                            selectedCategory ? [selectedCategory] : []
                        }
                        variant="search"
                        isMultiple={false}
                        handleAssignItem={(item) => {
                            setSelectedCategory(item);
                        }}
                        handleAddItem={(query: string) => {
                            searchCategories(query);
                        }}
                        itemsLoading={categoriesLoading}
                        handleRemoveItem={() => {
                            setSelectedCategory(null);
                        }}
                    />

                    {/* Delay is only configurable for Twitch Partners: 
              https://dev.twitch.tv/docs/api/reference#modify-channel-information */}
                    {twitch.user?.broadcaster_type === "partner" && (
                        <TextInput
                            id="twitch-delay"
                            type="number"
                            value={delay}
                            label={t("platforms:delay")}
                            placeholder={""}
                            onChange={(event) =>
                                handleDelayChange(event.target.value)
                            }
                        />
                    )}

                    {!!ingestServer && (
                        <div className="form-group">
                            <label htmlFor="twitch-ingest-server">
                                {t("platforms:ingest-server")}
                            </label>
                            <select
                                id="twitch-ingest-server"
                                className={`form-control`}
                                onChange={(event) =>
                                    onIngestChange(event.target.value)
                                }
                                value={ingestServer.name}
                                aria-describedby="ingest-help"
                            >
                                {twitch.ingestServers.map((s, i) => (
                                    <option key={"ingest-" + i} value={s.name}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                            <small id="ingest-help" className="text-center">
                                {t("misc:visit")}{" "}
                                <a href="https://stream.twitch.tv/ingests/">
                                    stream.twitch.tv/ingests
                                </a>{" "}
                                {t("misc:twitch-ingest")}
                            </small>
                        </div>
                    )}
                </>
            ) : (
                <PlatformSubsettingsBase
                    destinations={[user]}
                    platformId={PlatformId.Twitch}
                    imgSrcKey="profile_image_url"
                    destinationNameKey="display_name"
                    onDestinationClick={(d: DestinationDescriptor) => {
                        setSpecificDestination(d);
                        setHasAddedPlatformSpecificSettings(true);
                    }}
                    headingText={t("platforms:channels")}
                />
            )}
        </>
    );
};
